import { Point, type IAABBBounds, type Matrix } from '@visactor/vutils';
import { application } from '../application';
import type {
  IStage,
  GraphicAttributeMap,
  INode,
  IGraphic,
  ITheme,
  IThemeSpec,
  ILayer,
  GraphicType
} from '../interface';
import type { IGroup, IGroupGraphicAttribute } from '../interface/graphic/group';
import { Graphic, NOWORK_ANIMATE_ATTR } from './graphic';
import { getTheme, Theme } from './theme';
import { UpdateTag, IContainPointMode } from '../common/enums';
import { GROUP_NUMBER_TYPE } from './constants';
import { DefaultTransform } from './config';
import type { StateDefinitionsInput } from './state/state-definition';
import {
  type SharedStateScope,
  createGroupSharedStateScope,
  setSharedStateScopeLocalDefinitions,
  setSharedStateScopeParent
} from './state/shared-state-scope';
import { markScopeActiveDescendantsDirty } from './state/shared-state-refresh';

// Group更新AABBBounds的策略
export enum GroupUpdateAABBBoundsMode {
  // Group较少的情况，不会批量设置所有父层的tag，而是每次都查找
  LESS_GROUP = 0,
  // Group较多的情况，每次都会设置tag到最顶层
  MORE_GROUP = 1
}

export class Group extends Graphic<IGroupGraphicAttribute> implements IGroup {
  type: GraphicType = 'group';
  parent: any = null;
  isContainer: boolean = true;
  // 子元素的更新标记
  declare _childUpdateTag: number;

  declare theme?: ITheme;
  protected _sharedStateDefinitions?: StateDefinitionsInput<Record<string, any>>;
  declare sharedStateScope?: SharedStateScope<Record<string, any>>;

  static NOWORK_ANIMATE_ATTR = NOWORK_ANIMATE_ATTR;

  constructor(params: IGroupGraphicAttribute) {
    super(params);
    this.numberType = GROUP_NUMBER_TYPE;
    this._childUpdateTag = UpdateTag.UPDATE_BOUNDS;
    // this.theme = new Theme();
  }

  get sharedStateDefinitions(): StateDefinitionsInput<Record<string, any>> | undefined {
    return this._sharedStateDefinitions;
  }

  set sharedStateDefinitions(value: StateDefinitionsInput<Record<string, any>> | undefined) {
    if (this._sharedStateDefinitions === value) {
      return;
    }

    const previousScope = this.sharedStateScope;
    this._sharedStateDefinitions = value;
    this.ensureSharedStateScopeBound();
    if (this.sharedStateScope) {
      setSharedStateScopeLocalDefinitions(this.sharedStateScope, value);
      markScopeActiveDescendantsDirty(this.sharedStateScope, this.stage);
    }
    if (previousScope !== this.sharedStateScope) {
      this.notifyChildrenSharedStateTreeChanged();
    }
  }

  setMode(mode: '2d' | '3d') {
    mode === '3d' ? this.set3dMode() : this.set2dMode();
  }

  set3dMode() {
    this.in3dMode = true;
    // this.forEachChildren((c: IGraphic) => {
    //   c.setMode('3d');
    // });
  }
  set2dMode() {
    this.in3dMode = false;
    // this.forEachChildren((c: IGraphic) => {
    //   c.setMode('2d');
    // });
  }

  setTheme(t: IThemeSpec) {
    if (!this.theme) {
      this.theme = new Theme();
    }
    return this.theme.setTheme(t, this);
  }

  createTheme() {
    if (!this.theme) {
      this.theme = new Theme();
    }
  }

  visibleAll(visible: boolean) {
    this.setAttribute('visible', visible);
    this.forEachChildren((item: IGraphic) => {
      if (item.isContainer && (item as any).visibleAll) {
        (item as any).visibleAll(visible);
      } else {
        item.setAttribute('visible', visible);
      }
    });
  }

  hideAll() {
    this.visibleAll(false);
  }

  showAll() {
    this.visibleAll(true);
  }

  /**
   * 是否包含某个点（点需要是全局坐标系）
   * group containsPoint 只需要判断bounds
   * TODO: group的shape判断
   * @param x
   * @param y
   * @param mode
   * @returns
   */
  containsPoint(x: number, y: number, mode: IContainPointMode): boolean {
    if (mode === IContainPointMode.GLOBAL) {
      // 转换x，y更精准
      const point = new Point(x, y);
      if (this.parent) {
        this.parent.globalTransMatrix.transformPoint(point, point);
      }
      return this.AABBBounds.contains(point.x, point.y);
    }
    return this.AABBBounds.contains(x, y);
  }

  shouldUpdateAABBBounds(): boolean {
    // 检索自己是否需要更新
    if (super.shouldUpdateAABBBounds()) {
      return true;
    }
    // 检索叶子节点是否有更新（如果children是叶子节点的话）
    if (this._childUpdateTag & UpdateTag.UPDATE_BOUNDS) {
      return true;
    }
    return false;
    // // 检索是否子group需要更新
    // let needUpdate = false;
    // this.forEachChildren((node: IGraphic) => {
    //   // 只查找group层级
    //   if (node.isContainer && (node as Group).shouldUpdateAABBBounds()) {
    //     needUpdate = true;
    //     return true;
    //   }
    //   return false;
    // });
    // return needUpdate;
  }

  protected tryUpdateAABBBounds(): IAABBBounds {
    if (!this.shouldUpdateAABBBounds()) {
      return this._AABBBounds;
    }
    this.getGraphicService().beforeUpdateAABBBounds(this, this.stage, true, this._AABBBounds);
    const selfChange = this.shouldSelfChangeUpdateAABBBounds();
    const bounds = this.doUpdateAABBBounds();
    this.addUpdateLayoutTag();
    this.getGraphicService().afterUpdateAABBBounds(this, this.stage, this._AABBBounds, this, selfChange);
    // 直接返回空Bounds，但是前面的流程还是要走
    if (this.attribute.boundsMode === 'empty') {
      bounds.clear();
    }
    return bounds;
  }

  /**
   * 大部分group不需要更新matrix，这里特殊优化一下
   * 更新局部matrix的具体函数
   */
  protected doUpdateLocalMatrix() {
    const {
      x = DefaultTransform.x,
      y = DefaultTransform.y,
      dx = DefaultTransform.dx,
      dy = DefaultTransform.dy,
      scaleX = DefaultTransform.scaleX,
      scaleY = DefaultTransform.scaleY,
      angle = DefaultTransform.angle,
      postMatrix
    } = this.attribute;
    if (x === 0 && y === 0 && dx === 0 && dy === 0 && scaleX === 1 && scaleY === 1 && angle === 0 && !postMatrix) {
      this._transMatrix.reset();
      return;
    }
    return super.doUpdateLocalMatrix();
  }

  getGraphicTheme(): Required<IGroupGraphicAttribute> {
    return getTheme(this).group;
  }

  protected updateAABBBounds(
    attribute: IGroupGraphicAttribute,
    groupTheme: Required<IGroupGraphicAttribute>,
    aabbBounds: IAABBBounds
  ) {
    const originalAABBBounds = aabbBounds; // fix aabbbounds update error in flex layout
    aabbBounds = aabbBounds.clone();

    const { width, height, path, clip = groupTheme.clip } = attribute;
    // 添加自身的fill或者clip
    if (path && path.length) {
      path.forEach(g => {
        aabbBounds.union(g.AABBBounds);
      });
    } else if (width != null && height != null) {
      aabbBounds.set(0, 0, Math.max(0, width), Math.max(0, height)); // fix bounds set when auto size in vtable
    }
    if (!clip) {
      // 添加子节点
      this.forEachChildren((node: IGraphic) => {
        aabbBounds.union(node.AABBBounds);
      });
      // 如果没有clip的话，还需要加一下scroll
      const { scrollX = 0, scrollY = 0 } = attribute;
      aabbBounds.translate(scrollX, scrollY);
    }
    application.graphicService.updateTempAABBBounds(aabbBounds);

    application.graphicService.transformAABBBounds(attribute, aabbBounds, groupTheme, false, this);

    originalAABBBounds.copy(aabbBounds);
    return originalAABBBounds;
  }

  protected doUpdateAABBBounds(): IAABBBounds {
    this.updateAABBBoundsStamp++;
    const bounds = super.doUpdateAABBBounds();

    // 更新bounds之后需要设置父节点，否则tag丢失
    this.parent && this.parent.addChildUpdateBoundTag();
    this._emitCustomEvent('AAABBBoundsChange');

    return bounds;
  }

  protected clearUpdateBoundTag() {
    this._updateTag &= UpdateTag.CLEAR_BOUNDS;
    this._childUpdateTag &= UpdateTag.CLEAR_BOUNDS;
  }

  addUpdateBoundTag() {
    this._updateTag |= UpdateTag.UPDATE_BOUNDS; // for bounds
    if (this.parent) {
      this.parent.addChildUpdateBoundTag();
    }
  }

  addChildUpdateBoundTag() {
    // 如果已经设置过updateTag，那就不需要设置了
    if (this._childUpdateTag & UpdateTag.UPDATE_BOUNDS) {
      return;
    }
    // 如果没有设置过，那么继续向上设置
    this._childUpdateTag |= UpdateTag.UPDATE_BOUNDS;
    this.parent && this.parent.addChildUpdateBoundTag();
  }

  getTheme() {
    return this.theme.getTheme(this);
  }

  /* 场景树结构 */
  incrementalAppendChild(node: INode): INode | null {
    const data = super.appendChild(node);
    if (data) {
      this.syncChildSharedStateTreeBinding(data);
    }
    this.addUpdateBoundTag();
    this.getGraphicService().onAddIncremental(node as unknown as IGraphic, this, this.stage);
    return data;
  }
  incrementalClearChild(): void {
    super.removeAllChild();
    this.addUpdateBoundTag();
    this.getGraphicService().onClearIncremental(this, this.stage);
    return;
  }

  protected _updateChildToStage(child: IGraphic) {
    if (child) {
      this.syncChildSharedStateTreeBinding(child);
    }
    this.addUpdateBoundTag();
    return child;
  }
  // TODO 代码优化
  appendChild(node: INode, addStage: boolean = true): INode | null {
    const data = super.appendChild(node);
    if (data) {
      if (addStage) {
        this.syncChildSharedStateTreeBinding(data);
      }
    }
    this.addUpdateBoundTag();
    return data;
  }
  insertBefore(newNode: INode, referenceNode: INode): INode | null {
    return this._updateChildToStage(super.insertBefore(newNode, referenceNode) as undefined as IGraphic);
  }
  insertAfter(newNode: INode, referenceNode: INode): INode | null {
    return this._updateChildToStage(super.insertAfter(newNode, referenceNode) as undefined as IGraphic);
  }
  insertInto(newNode: INode, idx: number): INode | null {
    return this._updateChildToStage(super.insertInto(newNode, idx) as undefined as IGraphic);
  }

  removeChild(child: IGraphic, highPerformance: boolean = false): IGraphic {
    const data = super.removeChild(child);
    if (!data) {
      return data as IGraphic;
    }
    if (highPerformance) {
      child.detachStageForRelease();
      return data as IGraphic;
    }
    this.getGraphicService().onRemove(child);
    child.setStage(null as any, null as any);
    this.addUpdateBoundTag();
    return data as IGraphic;
  }

  removeAllChild(deep: boolean = false): void {
    const children = this.children.slice() as IGraphic[];
    this.forEachChildren((child: IGraphic) => {
      this.getGraphicService().onRemove(child);
      if (deep && child.isContainer) {
        child.removeAllChild(deep);
      }
    });
    super.removeAllChild();
    children.forEach(child => {
      child.setStage(null as any, null as any);
    });
    this.addUpdateBoundTag();
  }

  setStage(stage?: IStage, layer?: ILayer) {
    const graphicService = stage?.graphicService ?? this.stage?.graphicService ?? application.graphicService;
    const needsSharedStateTreeSync =
      this.hasSharedStateDefinitions() ||
      this.sharedStateScope ||
      this.currentStates?.length ||
      this.boundSharedStateScope ||
      this.registeredActiveScopes?.size ||
      this.sharedStateDirty;
    if (this.stage !== stage) {
      this.stage = stage;
      this.layer = layer;
      if (needsSharedStateTreeSync) {
        this.ensureSharedStateScopeBound();
        this.syncSharedStateScopeBindingOnTreeChange(true);
      }
      this.setStageToShadowRoot(stage, layer);
      this._onSetStage && this._onSetStage(this, stage, layer);
      graphicService?.onSetStage?.(this, stage);
      this.notifyChildrenSharedStateTreeChanged();
      return;
    }

    const layerChanged = this.layer !== layer;
    if (layerChanged) {
      this.layer = layer;
    }
    if (needsSharedStateTreeSync) {
      this.ensureSharedStateScopeBound();
      this.syncSharedStateScopeBindingOnTreeChange(true);
      this.notifyChildrenSharedStateTreeChanged();
    } else if (layerChanged) {
      this.notifyChildrenSharedStateTreeChanged();
    }
  }
  /**
   * 更新位置tag，包括全局tag和局部tag
   */
  addUpdatePositionTag() {
    super.addUpdatePositionTag();
    // 批量设置底层group的global tag
    this.forEachChildren((g: Group) => {
      if (g.isContainer) {
        g.addUpdateGlobalPositionTag();
      }
    });
  }
  /**
   * 更新全局位置tag
   */
  addUpdateGlobalPositionTag() {
    super.addUpdateGlobalPositionTag();
    // 批量设置底层group的global tag
    this.forEachChildren((g: Group) => {
      if (g.isContainer) {
        g.addUpdateGlobalPositionTag();
      }
    });
  }
  /**
   * group更新全局的transMatrix
   * @param clearTag
   * @returns
   */
  protected tryUpdateGlobalTransMatrix(clearTag: boolean = true): Matrix {
    if (this.shouldUpdateGlobalMatrix()) {
      if (!this._globalTransMatrix) {
        this._globalTransMatrix = this.parent
          ? (this.parent as IGroup).globalTransMatrix.clone()
          : this.transMatrix.clone();
      } else if (this.parent) {
        const m = (this.parent as IGroup).globalTransMatrix;
        this._globalTransMatrix.setValue(m.a, m.b, m.c, m.d, m.e, m.f);
      }
      this.doUpdateGlobalMatrix();
      clearTag && this.clearUpdateGlobalPositionTag();
    }
    return this._globalTransMatrix;
  }
  /**
   * 查找自身更新global的tag，如果存在，就更新
   * @returns
   */
  shouldUpdateGlobalMatrix(): boolean {
    const shouldUpdate = !!(this._updateTag & UpdateTag.UPDATE_GLOBAL_MATRIX);
    return shouldUpdate;
  }

  private _getChildByName<T extends INode = INode>(name: string, deep?: boolean): T | null {
    return this.find(node => node.name === name, deep);
  }

  /**
   * if graphic exist then update attributes, otherwise create a new instance
   * @param graphicName the name of graphic
   * @param attributes the attributes of graphic
   * @param graphicType the type of graphic
   * @returns the graphic instance
   */
  createOrUpdateChild<T extends keyof GraphicAttributeMap>(
    graphicName: string,
    attributes: GraphicAttributeMap[T],
    graphicType: T
  ): INode {
    let graphic = this._getChildByName(graphicName) as IGraphic;
    if (graphic) {
      graphic.setAttributes(attributes);
    } else {
      graphic = application.graphicService.creator[graphicType](attributes as any);
      graphic.name = graphicName;
      this.add(graphic);
    }

    return graphic;
  }

  clone() {
    return new Group({ ...this.attribute });
  }

  getNoWorkAnimateAttr(): Record<string, number> {
    return Group.NOWORK_ANIMATE_ATTR;
  }

  release(all?: boolean) {
    if (all) {
      this.forEachChildren((g: IGraphic) => {
        g.release(all);
      });
    }
    super.release();
  }

  detachStageForRelease() {
    super.detachStageForRelease();
    this.sharedStateScope = undefined;
    this.forEachChildren((item: IGraphic) => {
      item.detachStageForRelease();
    });
  }

  protected ensureSharedStateScopeBound(): void {
    if (!this.hasSharedStateDefinitions()) {
      this.sharedStateScope = undefined;
      return;
    }

    const parentScope = (this.parent as any)?.sharedStateScope ?? this.stage?.rootSharedStateScope;
    if (!this.sharedStateScope) {
      this.sharedStateScope = createGroupSharedStateScope(this, parentScope, this._sharedStateDefinitions);
      return;
    }

    this.sharedStateScope.ownerStage = this.stage;
    setSharedStateScopeParent(this.sharedStateScope, parentScope);

    if (this.sharedStateScope.localStateDefinitions !== this._sharedStateDefinitions) {
      setSharedStateScopeLocalDefinitions(this.sharedStateScope, this._sharedStateDefinitions);
    }
  }

  protected hasSharedStateDefinitions(): boolean {
    return !!this._sharedStateDefinitions && Object.keys(this._sharedStateDefinitions).length > 0;
  }

  protected notifyChildrenSharedStateTreeChanged(): void {
    this.forEachChildren(item => {
      this.syncChildSharedStateTreeBinding(item);
    });
  }

  protected syncChildSharedStateTreeBinding(child: INode): void {
    child.onParentSharedStateTreeChanged(this.stage, this.layer);
  }

  onParentSharedStateTreeChanged(stage?: IStage, layer?: ILayer): void {
    if (this.stage !== stage || this.layer !== layer) {
      this.setStage(stage, layer);
      return;
    }

    this.ensureSharedStateScopeBound();
    this.syncSharedStateScopeBindingOnTreeChange(!!this.currentStates?.length);
    this.notifyChildrenSharedStateTreeChanged();
  }
}

export function createGroup(attributes: IGroupGraphicAttribute): IGroup {
  return new Group(attributes);
}
